import React, { memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Modal from 'components/Modal';
import { uploadButton as TEXT } from 'locales/en';

const UploadButton = ({
  testId,
  label,
  name,
  file,
  onSelectFile,
  onDeleteFile,
  docType,
  error,
  labelClassName,
  hideDeleteButton
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const fileRef = useRef(null);
  const handleUpload = () => {
    fileRef.current?.click();
  };

  const handleSelectFile = (e) => {
    if (e.target.files?.length && onSelectFile) {
      onSelectFile(e.target.files[0]);
    }
  };

  const handleDeleteFile = () => {
    setIsOpen(true);
  };

  const _onDeleteFile = () => {
    setIsOpen(false);
    if (onDeleteFile) {
      onDeleteFile();
    }
  };

  return (
    <>
      <div>
        <label
          htmlFor={name}
          className="flex flex-1 flex-row items-center justify-between"
          data-testid={testId}
        >
          <span className={`text-xs text-javelin-gray-light ${labelClassName}`}>
            {label}
          </span>
          <div>
            <Button
              title={TEXT.upload}
              onClick={handleUpload}
              className="btn-secondary px-6 py-3 text-sm"
            />
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={handleSelectFile}
              value={''}
              accept=".png, .jpg, .jpeg, .pdf"
              data-testid="file-input"
              name={name}
            />
          </div>
        </label>
        {!!error && (
          <ErrorMessage
            message={error}
            className="ml-auto mr-2 w-fit-content lg:mt-2"
          />
        )}
        {!!file && (
          <div className="mt-4 flex flex-col lg:flex-row lg:items-center">
            <span className="pb-1 text-xs text-javelin-gray-light lg:w-4/12 lg:pb-0">
              Uploading Document {docType}
            </span>
            <div
              className="card bg-white py-4 px-7 lg:w-8/12"
              data-testid="file-info"
            >
              <div className="flex items-center justify-between">
                <FontAwesomeIcon
                  icon={['far', 'file']}
                  size="sm"
                  className="whitespace-nowrap"
                />
                <p className="ml-5 inline-block flex-1 truncate text-xs font-bold text-javelin-gray-dark">
                  {file.name}
                </p>
                <div className="pl-4">
                  <span className="mr-5 text-xs text-javelin-gray-light">
                    {Math.round(file.size / 1024)}kb
                  </span>
                  <FontAwesomeIcon
                    icon={['far', 'trash-alt']}
                    size="sm"
                    className={`cursor-pointer text-javelin-gray-light ${hideDeleteButton ? 'invisible' : ''}`}
                    onClick={handleDeleteFile}
                    data-testid={`delete-icon${hideDeleteButton ? '-invisible' : ''}`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Modal
        isOpen={isOpen}
        title={TEXT.adminModalTitle}
        onCancel={() => setIsOpen(false)}
        onConfirm={_onDeleteFile}
      />
    </>
  );
};

UploadButton.propTypes = {
  className: PropTypes.string,
  testId: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onDeleteFile: PropTypes.func,
  onSelectFile: PropTypes.func,
  error: PropTypes.string,
  hideDeleteButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.any])
};
UploadButton.defaultProps = {
  className:
    'bg-javelin-blue box-content border-javelin-blue rounded rounded-l-none h-full px-0 py-px',
  testId: 'upload-button',
  label: '',
  name: 'upload-button',
  file: null,
  onDeleteFile: () => null,
  onSelectFile: () => null,
  error: null,
  labelClassName: 'pb-1 lg:pb-0',
  hideDeleteButton: false
};

export default memo(UploadButton);
